import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { useInView } from 'react-intersection-observer';
import { SectionHeader } from '../ui/SectionHeader';

export const ContactSection: React.FC = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    institutionName: '',
    contactName: '',
    email: '',
    phone: '',
    role: '',
    partnershipType: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = `طلب شراكة مؤسسية - ${formData.institutionName}`;
    const body = `
السلام عليكم ورحمة الله وبركاته،

أتقدم بطلب شراكة مؤسسية مع منصة ضوء الخريج:

معلومات المؤسسة:
- اسم المؤسسة: ${formData.institutionName}
- نوع الشراكة: ${formData.partnershipType}

معلومات المسؤول:
- الاسم: ${formData.contactName}
- المنصب: ${formData.role}
- البريد الإلكتروني: ${formData.email}
- رقم الهاتف: ${formData.phone}

تفاصيل الطلب:
${formData.message}

نتطلع للتواصل معكم قريباً.

مع أطيب التحيات
    `.trim();

    const mailtoLink = `mailto:info@alkhreej.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const partnershipOptions = [
    { value: '', label: 'اختر نوع الشراكة' },
    { value: 'university', label: 'شراكة جامعية' },
    { value: 'government', label: 'شراكة حكومية' },
    { value: 'school', label: 'شراكة مدرسية' },
    { value: 'corporate', label: 'شراكة مؤسسية' },
    { value: 'other', label: 'أخرى' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="section-container">
        <SectionHeader
          subtitle="تواصل معنا"
          title="ابدأ رحلة الشراكة المؤسسية"
          description="نحن مستعدون لمناقشة كيفية دعم مؤسستكم في تحقيق أهدافها التعليمية والمهنية"
        />

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="mt-12 lg:mt-16 flex justify-center"
        >
          {/* Contact Form */}
          <motion.div variants={itemVariants} className="w-full max-w-4xl">
            <div className="card-government">
              <h3 className="text-2xl font-bold text-government-navy mb-6 font-heading">
                طلب شراكة مؤسسية
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-government-navy mb-2">
                      اسم المؤسسة *
                    </label>
                    <input
                      type="text"
                      name="institutionName"
                      value={formData.institutionName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="مثال: جامعة الملك سعود"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-government-navy mb-2">
                      اسم المسؤول *
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="الاسم الكامل"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-government-navy mb-2">
                      البريد الإلكتروني *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="example@university.edu.sa"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-government-navy mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="+966 5X XXX XXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-government-navy mb-2">
                      المنصب
                    </label>
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                      placeholder="مثال: مدير التطوير الأكاديمي"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-government-navy mb-2">
                      نوع الشراكة المطلوبة *
                    </label>
                    <select
                      name="partnershipType"
                      value={formData.partnershipType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    >
                      {partnershipOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-government-navy mb-2">
                    تفاصيل الطلب
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200"
                    placeholder="يرجى توضيح أهداف الشراكة والخدمات المطلوبة..."
                  />
                </div>

                <div className="flex justify-start font-heading">
                  <button
                    type="submit"
                    className="btn-primary flex-1 justify-center flex items-center gap-2"
                  >
                    <FiSend />
                    إرسال طلب الشراكة
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};